import {
  LayoutDashboard,
  Users,
  Building2,
  UserPlus,
  FileText,
  Wallet,
  UserCircle,
  ClipboardList,
  Settings,
  Radio,
  Link2,
  Newspaper,
  MessageSquareText,
  MessageCircle,
  ImageIcon,
  Shield,
  TrendingUp,
  FolderCog,
} from "lucide-react";

/**
 * Portal home path for each Spatie role.
 * director/manager share the admin portal (menus filtered by permissions).
 */
export const ROLE_HOME = {
  admin: "/admin",
  director: "/admin",
  manager: "/admin",
  ftm: "/ftm",
  church: "/church",
  setmember: "/set-member",
  setleader: "/set-leader",
  pcf: "/profile",
  cell: "/profile",
  user: "/profile",
};

export function roleHomePath(role) {
  if (!role) return "/login";
  return ROLE_HOME[role] || "/profile";
}

/** Roles that use the admin shell /admin/* */
export const ADMIN_PORTAL_ROLES = ["admin", "director", "manager"];

/** Roles with unrestricted portal access (skip permission filters). */
export const SUPER_ROLES = ["admin", "director"];

/**
 * Navigation catalog. Items are shown when the user's role portal matches
 * and they hold the required permission(s) (unless super role).
 */
export const NAV_ITEMS = [
  // Admin / director / manager portal
  {
    icon: LayoutDashboard,
    label: "Dashboard",
    href: "/admin",
    portals: ["admin"],
  },
  {
    icon: TrendingUp,
    label: "Attendance",
    href: "/admin/analytics",
    portals: ["admin"],
  },
  {
    icon: Building2,
    label: "Churches",
    href: "/admin/churches",
    portals: ["admin"],
    permissions: ["view-churches"],
  },
  {
    icon: UserPlus,
    label: "FTMs",
    href: "/admin/ftms",
    portals: ["admin"],
    permissions: ["view-first-contacts"],
  },
  {
    icon: Users,
    label: "Users",
    href: "/admin/users",
    portals: ["admin"],
    permissions: ["view-users"],
  },
  {
    icon: FileText,
    label: "Reports",
    href: "/admin/reports",
    portals: ["admin"],
    permissions: ["view-reports"],
  },
  {
    icon: Radio,
    label: "Live programs",
    href: "/admin/live-programs",
    portals: ["admin"],
    permissions: ["view-live-programs"],
  },
  {
    icon: Newspaper,
    label: "News & Videos",
    href: "/admin/member-updates",
    portals: ["admin"],
    permissions: ["view-posts"],
  },
  {
    icon: MessageSquareText,
    label: "Messages",
    href: "/admin/messages",
    portals: ["admin"],
    permissions: ["view-messages"],
  },
  {
    icon: ImageIcon,
    label: "E-cards",
    href: "/admin/ecards",
    portals: ["admin"],
    permissions: ["view-ecards"],
  },
  {
    icon: MessageCircle,
    label: "Contact details",
    href: "/admin/contact",
    portals: ["admin"],
    permissions: ["view-settings"],
  },
  {
    icon: FolderCog,
    label: "Manage",
    portals: ["admin"],
    children: [
      {
        icon: Shield,
        label: "Roles & permissions",
        href: "/admin/roles",
        permissions: ["view-roles"],
      },
      {
        icon: Wallet,
        label: "Expenses",
        href: "/admin/expenses",
        permissions: ["view-expenses"],
      },
      {
        icon: Link2,
        label: "Stream links",
        href: "/admin/stream-links",
        permissions: ["view-live-programs"],
      },
    ],
  },
  {
    icon: Settings,
    label: "Settings",
    href: "/admin/settings",
    portals: ["admin"],
    permissions: ["view-settings"],
  },

  // FTM portal
  {
    icon: LayoutDashboard,
    label: "Dashboard",
    href: "/ftm",
    portals: ["ftm"],
  },
  {
    icon: UserPlus,
    label: "FTMs",
    href: "/ftm/ftms",
    portals: ["ftm"],
    permissions: ["view-first-contacts"],
  },
  {
    icon: Users,
    label: "Members",
    href: "/ftm/members",
    portals: ["ftm"],
    permissions: ["view-ftm-setmembers"],
  },
  {
    icon: Users,
    label: "Leaders",
    href: "/ftm/leaders",
    portals: ["ftm"],
    permissions: ["view-ftm-setleaders"],
  },
  {
    icon: ClipboardList,
    label: "Follow-ups",
    href: "/ftm/followups",
    portals: ["ftm"],
    permissions: ["view-followups"],
  },

  // Church portal
  {
    icon: LayoutDashboard,
    label: "Dashboard",
    href: "/church",
    portals: ["church"],
  },
  {
    icon: FileText,
    label: "Service Reports",
    href: "/church/reports",
    portals: ["church"],
    permissions: ["view-reports"],
  },
  {
    icon: Wallet,
    label: "Expenses",
    href: "/church/expenses",
    portals: ["church"],
    permissions: ["view-expenses"],
  },
  {
    icon: UserPlus,
    label: "FTMs",
    href: "/church/ftms",
    portals: ["church"],
    permissions: ["view-first-contacts"],
  },

  // Set member
  {
    icon: LayoutDashboard,
    label: "Dashboard",
    href: "/set-member",
    portals: ["setmember"],
  },
  {
    icon: UserPlus,
    label: "My FTMs",
    href: "/set-member/ftms",
    portals: ["setmember"],
    permissions: ["view-first-contacts"],
  },
  {
    icon: ClipboardList,
    label: "Follow-ups",
    href: "/set-member/followups",
    portals: ["setmember"],
    permissions: ["view-followups"],
  },

  // Set leader
  {
    icon: LayoutDashboard,
    label: "Dashboard",
    href: "/set-leader",
    portals: ["setleader"],
  },
  {
    icon: UserPlus,
    label: "Assigned FTMs",
    href: "/set-leader/ftms",
    portals: ["setleader"],
    permissions: ["view-first-contacts"],
  },
  {
    icon: ClipboardList,
    label: "Monitoring",
    href: "/set-leader/monitoring",
    portals: ["setleader"],
    permissions: ["view-monitorings"],
  },

  // Always available
  {
    icon: Newspaper,
    label: "News & Videos",
    href: "/updates",
    portals: ["member", "ftm", "church", "setmember", "setleader"],
  },
  {
    icon: Radio,
    label: "Live",
    href: "/live",
    portals: ["member", "ftm", "church", "setmember", "setleader"],
  },
  {
    icon: UserCircle,
    label: "Profile",
    href: "/profile",
    portals: ["admin", "ftm", "church", "setmember", "setleader", "member"],
  },
];

/** Which portal shell a role uses for menu filtering */
export function portalForRole(role) {
  if (!role) return "member";
  if (ADMIN_PORTAL_ROLES.includes(role)) return "admin";
  if (["ftm", "church", "setmember", "setleader"].includes(role)) return role;
  return "member";
}

export function normalizePermissions(user) {
  const raw = user?.permissions;
  if (!raw) return [];
  if (Array.isArray(raw)) {
    return raw.map((p) => (typeof p === "string" ? p : p?.name)).filter(Boolean);
  }
  return [];
}

export function normalizeRoles(user) {
  const fromList = user?.roles;
  if (Array.isArray(fromList) && fromList.length) {
    return fromList.map((r) => (typeof r === "string" ? r : r?.name)).filter(Boolean);
  }
  return user?.role ? [user.role] : [];
}

export function createAccessHelpers(user) {
  const permissions = normalizePermissions(user);
  const roles = normalizeRoles(user);
  const primaryRole = user?.role || roles[0] || null;
  const portal = portalForRole(primaryRole);
  const isSuper = roles.some((r) => SUPER_ROLES.includes(r));

  const hasRole = (name) => roles.includes(name);
  const hasAnyRole = (names = []) => names.some((n) => roles.includes(n));
  const hasPermission = (name) => isSuper || permissions.includes(name);
  const hasAnyPermission = (names = []) =>
    !names.length || isSuper || names.some((n) => permissions.includes(n));
  const hasAllPermissions = (names = []) =>
    !names.length || isSuper || names.every((n) => permissions.includes(n));

  /**
   * Route / menu gate.
   * - Super roles (admin, director) always pass.
   * - If `roles` given, user must match one (manager included for admin portal).
   * - If `permissions` given, user must hold at least one.
   */
  const canAccess = ({ roles: needRoles, permissions: needPerms } = {}) => {
    if (isSuper) return true;
    if (needRoles?.length && !hasAnyRole(needRoles)) return false;
    if (needPerms?.length && !hasAnyPermission(needPerms)) return false;
    return true;
  };

  const menuItems = NAV_ITEMS.map((item) => {
    if (!item.portals.includes(portal) && !(portal === "member" && item.href === "/profile")) {
      return null;
    }

    if (Array.isArray(item.children) && item.children.length) {
      const children = item.children.filter(
        (child) => !child.permissions?.length || hasAnyPermission(child.permissions)
      );
      if (!children.length) return null;
      return { ...item, children };
    }

    if (item.permissions?.length && !hasAnyPermission(item.permissions)) {
      return null;
    }

    return item;
  }).filter(Boolean);
  return {
    permissions,
    roles,
    primaryRole,
    portal,
    isSuper,
    hasRole,
    hasAnyRole,
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    canAccess,
    menuItems,
    homePath: user?.home_path || roleHomePath(primaryRole),
  };
}
